import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Box, IconButton, Link, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import useGalleryStyle from "./ContentGallery.styles";
import {
  GalleryAppliedChip,
  GalleryFilterValue,
  GallerySortValue,
  buildFilterChips,
  buildSortChip,
} from "./utils/gallerySortFilter";

type GalleryFilterChipsProps = {
  selectedCDPTags: string[];
  selectedTags: string[];
  searchTerm?: string;
  sortValue?: GallerySortValue | null;
  filterValue?: GalleryFilterValue | null;
  // eslint-disable-next-line no-unused-vars
  onRemoveCDPTag: (tag: string) => void;
  // eslint-disable-next-line no-unused-vars
  onRemoveTag: (tag: string) => void;
  onRemoveSearch: () => void;
  /** Drops the whole applied date range - both ends are one filter. */
  onRemoveDateRange: () => void;
  // eslint-disable-next-line no-unused-vars
  onRemoveAuthor: (value: string) => void;
  onRemoveSort: () => void;
  onClearAll: () => void;
  onClearFilters: () => void;
};

const GalleryFilterChips = ({
  selectedCDPTags = [],
  selectedTags = [],
  searchTerm = "",
  sortValue = null,
  filterValue = null,
  onRemoveCDPTag,
  onRemoveTag,
  onRemoveSearch,
  onRemoveDateRange,
  onRemoveAuthor,
  onRemoveSort,
  onClearAll,
  onClearFilters,
}: GalleryFilterChipsProps) => {
  const { t } = useTranslation();
  const classes = useGalleryStyle();

  const hasTagFilters =
    selectedCDPTags.length > 0 || selectedTags.length > 0 || Boolean(searchTerm);
  const filterChips = buildFilterChips(filterValue);
  const sortChip = buildSortChip(sortValue);

  if (!hasTagFilters && filterChips.length === 0 && !sortChip) return null;

  /** Which handler a "Filtered by" chip's cross calls. */
  const removeFilterChip = (chip: GalleryAppliedChip) =>
    chip.kind === "dateRange" ? onRemoveDateRange : () => onRemoveAuthor(chip.value as string);

  const chipLabel = (chip: GalleryAppliedChip) => {
    const value = chip.valueKeys ? chip.valueKeys.map((key) => t(key)).join(" - ") : chip.valueText;
    return chip.groupKey ? `${t(chip.groupKey)}: ${value}` : `${value}`;
  };

  const renderChip = (
    label: string,
    key: string,
    onRemove: () => void,
    testId: string,
    applied = false,
  ) => (
    <Box
      key={key}
      className={`${classes.filterChip} ${applied ? classes.filterChipApplied : ""}`}
      data-testid={testId}>
      <Typography variant='p4regular' component='span' className='filterChipLabel'>
        {label}
      </Typography>
      <IconButton
        size='small'
        onClick={onRemove}
        aria-label={`${t("remove")} ${label}`}
        data-testid={`${testId}-remove`}
        className='filterChipRemove'>
        <CloseRoundedIcon sx={{ fontSize: "14px" }} />
      </IconButton>
    </Box>
  );

  const renderClearAll = (onClear: () => void, testId: string) => (
    <Link
      component='button'
      type='button'
      underline='always'
      variant='p4medium'
      onClick={onClear}
      data-testid={testId}
      className={classes.filterChipsClearAll}>
      {t("clear_all")}
    </Link>
  );

  return (
    <Box className={classes.filterChipsStack} data-testid='gallery-filter-chips'>
      {hasTagFilters ? (
        <Box className={classes.filterChipsRow} data-testid='gallery-chips-row-tags'>
          <Typography variant='h6semibold' sx={{ marginRight: "4px" }}>
            {t("showing_results_for")}
          </Typography>

          {selectedCDPTags.map((tag) =>
            renderChip(
              tag,
              `cdp-${tag}`,
              () => onRemoveCDPTag(tag),
              `gallery-filter-chip-cdp-${tag}`,
            ),
          )}

          {selectedTags.map((tag) =>
            renderChip(tag, `tag-${tag}`, () => onRemoveTag(tag), `gallery-filter-chip-tag-${tag}`),
          )}

          {searchTerm
            ? renderChip(
                `${t("search")}: "${searchTerm}"`,
                "search-term",
                onRemoveSearch,
                "gallery-filter-chip-search",
              )
            : null}

          {renderClearAll(onClearAll, "gallery-filter-clear-all")}
        </Box>
      ) : null}

      {filterChips.length > 0 ? (
        <Box className={classes.filterChipsRow} data-testid='gallery-chips-row-filtered-by'>
          <Typography variant='h6semibold' sx={{ marginRight: "4px" }}>
            {t("filtered_by")}
          </Typography>

          {filterChips.map((chip) =>
            renderChip(
              chipLabel(chip),
              chip.id,
              removeFilterChip(chip),
              `gallery-filter-chip-${chip.id}`,
              true,
            ),
          )}

          {renderClearAll(onClearFilters, "gallery-filtered-by-clear-all")}
        </Box>
      ) : null}

      {sortChip ? (
        <Box className={classes.filterChipsRow} data-testid='gallery-chips-row-sort-by'>
          <Typography variant='h6semibold' sx={{ marginRight: "4px" }}>
            {`${t("sort_by")}:`}
          </Typography>

          {renderChip(
            chipLabel(sortChip),
            sortChip.id,
            onRemoveSort,
            "gallery-filter-chip-sort",
            true,
          )}

          {renderClearAll(onRemoveSort, "gallery-sort-by-clear-all")}
        </Box>
      ) : null}
    </Box>
  );
};

export default GalleryFilterChips;
